"use client";

import { useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { PeriodicDetail } from "@/components/tools/chem-reference/periodic-detail";
import { PeriodicGrid } from "@/components/tools/chem-reference/periodic-grid";
import { PeriodicList } from "@/components/tools/chem-reference/periodic-list";
import {
  CLASS_FILL,
  TILE_TEXT,
} from "@/components/tools/chem-reference/periodic-style";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  CLASSES_BY_MODE,
  COLOR_MODES,
  type ColorMode,
  classCounts,
  matchElement,
  PERIODIC_ELEMENTS,
} from "@/lib/tool-kit/chem-reference/periodic";

/** English strings. A translated content file overrides these through `ui`. */
export const PERIODIC_TABLE_STRINGS = {
  searchLabel: "Search the elements",
  searchHint:
    "Type a name, a symbol, an atomic number or a category — try Fe, 26, gold or halogen.",
  searchPlaceholder: "Fe, iron, 26, halogen",
  colorLabel: "Colour the elements by",
  colorModes: {
    category: "Category",
    block: "Block",
    electronegativity: "Electronegativity (Pauling)",
    discovery: "Year of discovery",
  },
  viewLabel: "Show the elements as",
  viewGrid: "Periodic table",
  viewList: "List with properties",
  matchCount: "one: {count} element matches|other: {count} elements match",
  noMatches:
    "No element matches that search. Check the spelling or try a symbol.",
  legendHeading: "Legend: {mode}",
  legendCount: "one: {count} element|other: {count} elements",
  gridLabel:
    "Periodic table of the elements. Tab into the table, then use the arrow keys to move between elements and Enter to select one.",
  gridNote:
    "Group 3 holds Sc, Y, Lu and Lr (IUPAC 2021 provisional recommendation); the two rows below the table are the f-block, La–Yb and Ac–No. Scroll sideways on a narrow screen.",
  cellLabel: "{name}, symbol {symbol}, atomic number {z}, {class}",
  classLabels: {
    alkali: "Alkali metal",
    alkaline: "Alkaline earth metal",
    transition: "Transition metal",
    post: "Post-transition metal",
    metalloid: "Metalloid",
    nonmetal: "Nonmetal",
    halogen: "Halogen",
    noble: "Noble gas",
    lanthanide: "Lanthanide",
    actinide: "Actinide",
    blockS: "s-block",
    blockP: "p-block",
    blockD: "d-block",
    blockF: "f-block",
    en1: "Electronegativity below 1.0",
    en2: "Electronegativity 1.0 to 1.49",
    en3: "Electronegativity 1.5 to 1.99",
    en4: "Electronegativity 2.0 to 2.49",
    en5: "Electronegativity 2.5 to 2.99",
    en6: "Electronegativity 3.0 or above",
    enNone: "No Pauling value",
    ancient: "Known since antiquity",
    before1800: "Discovered before 1800",
    y1800s: "Discovered 1800 to 1899",
    y1900a: "Discovered 1900 to 1949",
    y1950: "Discovered 1950 or later",
  },
  blockNames: {
    s: "s-block",
    p: "p-block",
    d: "d-block",
    f: "f-block",
  },
  detailHeading: "Selected element",
  atomicNumber: "Atomic number (Z)",
  atomicWeight: "Atomic weight",
  weightUnit: "u",
  massNumberNote:
    "No IUPAC standard atomic weight exists for this element, so the bracketed number is the mass number of its longest-lived known isotope.",
  group: "Group",
  groupNone: "none (f-block)",
  period: "Period",
  block: "Block",
  category: "Category",
  electronegativity: "Electronegativity (Pauling)",
  electronegativityNone: "no accepted value",
  configuration: "Ground-state configuration",
  expanded: "Full configuration",
  predictedNote:
    "No ground-state configuration has been measured for this element; the one shown is the predicted Madelung-order filling.",
  discovered: "Discovered",
  ancient: "Known since antiquity",
  copyDetails: "Copy these details",
  copied: "Copied!",
  copyText:
    "{name} ({symbol}), Z = {z}, atomic weight {weight}, Pauling electronegativity {en}, configuration {config}",
  columnNumber: "Z",
  columnSymbol: "Symbol",
  columnName: "Name",
  columnWeight: "Atomic weight",
  columnGroup: "Group",
  columnPeriod: "Period",
  columnBlock: "Block",
  columnCategory: "Category",
  columnElectronegativity: "Pauling EN",
  columnDiscovered: "Discovered",
  dash: "—",
  listCaption:
    "All elements in atomic-number order. Select a name to see its details.",
  sourcesNote:
    "Atomic weights: IUPAC/CIAAW abridged standard atomic weights 2024. Electronegativity, category and discovery year: PubChem Periodic Table (NCBI). Configurations: NIST Atomic Spectra Database.",
} as const;

const DEFAULT_ELEMENT = 6;

export default function PeriodicTableWidget() {
  const strings = useToolUi(PERIODIC_TABLE_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<ColorMode>("category");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [selected, setSelected] = useState(DEFAULT_ELEMENT);

  const filtering = query.trim() !== "";
  const matching = PERIODIC_ELEMENTS.filter((element) =>
    matchElement(element, query, strings.classLabels[element.category])
  );
  const matchSet = new Set(matching.map((element) => element.z));
  const classes = CLASSES_BY_MODE[mode];
  const counts = classCounts(mode);
  const element = PERIODIC_ELEMENTS[selected - 1];

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.searchHint}
          id="pt-search"
          label={strings.searchLabel}
          onChange={setQuery}
          placeholder={strings.searchPlaceholder}
          type="search"
          value={query}
        />
        <SelectField
          id="pt-color"
          label={strings.colorLabel}
          onChange={setMode}
          options={COLOR_MODES.map((value) => ({
            value,
            label: strings.colorModes[value],
          }))}
          value={mode}
        />
      </div>
      <PillGroup
        label={strings.viewLabel}
        name="pt-view"
        onChange={setView}
        options={[
          { value: "grid", label: strings.viewGrid },
          { value: "list", label: strings.viewList },
        ]}
        value={view}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.detailHeading}</h2>
        <p className="text-muted-foreground text-sm">
          {filtering
            ? t(
                tPlural(strings.matchCount, locale, matching.length, {
                  count: fmt(matching.length),
                }),
                {}
              )
            : null}
        </p>
        {filtering && matching.length === 0 ? (
          <Notice>{strings.noMatches}</Notice>
        ) : null}
        <PeriodicDetail
          categoryId={element.category}
          element={element}
          fmt={fmt}
          strings={strings}
        />
      </section>
      <section className="space-y-3">
        <h3 className="font-semibold text-base text-foreground">
          {t(strings.legendHeading, { mode: strings.colorModes[mode] })}
        </h3>
        <ul className="flex flex-wrap gap-2 text-xs">
          {classes.map((cls) => (
            <li
              className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1 text-foreground"
              key={cls.id}
            >
              <span
                aria-hidden="true"
                className={`inline-flex h-6 min-w-6 items-center justify-center rounded border border-black/15 px-1 font-semibold ${CLASS_FILL[cls.id]} ${TILE_TEXT}`}
              >
                {cls.code}
              </span>
              <span>
                {
                  strings.classLabels[
                    cls.id as keyof typeof strings.classLabels
                  ]
                }
                {" · "}
                {tPlural(strings.legendCount, locale, counts[cls.id], {
                  count: fmt(counts[cls.id]),
                })}
              </span>
            </li>
          ))}
        </ul>
      </section>
      {view === "grid" ? (
        <div className="space-y-2">
          <div className="overflow-x-auto pb-2">
            <div className="min-w-[48rem]">
              <PeriodicGrid
                classes={classes}
                filtering={filtering}
                matches={matchSet}
                mode={mode}
                onSelect={setSelected}
                selected={selected}
                strings={strings}
              />
            </div>
          </div>
          <p className="text-muted-foreground text-xs">{strings.gridNote}</p>
        </div>
      ) : (
        <PeriodicList
          elements={matching}
          fmt={fmt}
          onSelect={setSelected}
          selected={selected}
          strings={strings}
        />
      )}
      <p className="text-muted-foreground text-xs">{strings.sourcesNote}</p>
    </>
  );
}
